import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import { workoutPlannerAPI } from '../../services/api'

/**
 * VH-134: which of this module's exercises its location cannot do.
 *
 * Never blocks. Same pattern as VH-130's duplicate-machine guard and VH-125's
 * muscle cross-check — the app flags, the user judges. Drafting a session
 * before a gym has been scanned is the normal order of work, not an error, and
 * you might legitimately bring your own kit.
 *
 * The remedy offered is to swap in an exercise you CAN do here, not to attach a
 * machine to the offending exercise: the module belongs to the place, so the
 * place is fixed and the exercise is the variable.
 */
export default function ModuleLocationWarning({ template, onReplace }) {
  const { t } = useTranslation()

  const { data } = useQuery({
    queryKey: ['templateLocationCheck', template?.id],
    queryFn: () =>
      workoutPlannerAPI.templateLocationCheck(template.id).then((r) => r.data),
    // Only worth asking when the module is actually tied to a place. An
    // outdoor module is not a module with a problem.
    enabled: Boolean(template?.id && template?.location_id),
  })

  if (!data || data.all_possible || !data.not_possible?.length) return null

  return (
    <div
      className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-3"
      data-testid={`location-warning-${template.id}`}
    >
      <p className="flex items-start gap-2 text-sm font-medium text-amber-800">
        <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
        {t('trainingLocations.module.notPossibleIntro', {
          location: data.location_name,
        })}
      </p>

      <ul className="mt-2 space-y-1">
        {data.not_possible.map((item) => (
          <li
            key={item.exercise_id}
            className="flex flex-wrap items-center gap-2 text-sm text-amber-900"
            data-testid={`not-possible-${item.exercise_id}`}
          >
            <span>{item.name_en}</span>
            {onReplace && (
              <button
                type="button"
                onClick={() => onReplace(item, data)}
                className="text-xs font-medium text-primary-600 hover:text-primary-700 underline"
                data-testid={`replace-${item.exercise_id}`}
              >
                {t('trainingLocations.module.replaceWith')}
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
